import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  DoorOpen,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import LocationsSettingsSkeleton from "./skeletons/LocationsSettingsSkeleton";
import { CharLimitMessage } from "@/components/common/CharLimitMessage";
import { LOCATION_LIMITS } from "@/lib/settings/settingsLimits";
import useTitanApp from "@/hooks/useTitanApp";
import { useForm } from "react-hook-form";
import {
  addRoom as addPharmacyRoom,
  getPharmaciesByCompanyId,
  getCompanyPharmaciesCount,
} from "@/dispatchers/locationDispatchers";
import { updateIsActiveByUserEmailCode } from "@/dispatchers/onboardingVerificationDsipatchers";
import { showError } from "@/components/ToastedMessage/ToastedMessage";
// TypeScript interfaces for location settings
interface TimeBlock {
  from: string;
  to: string;
}

interface RoomAvailability {
  monday: { enabled: boolean; timeBlocks: TimeBlock[] };
  tuesday: { enabled: boolean; timeBlocks: TimeBlock[] };
  wednesday: { enabled: boolean; timeBlocks: TimeBlock[] };
  thursday: { enabled: boolean; timeBlocks: TimeBlock[] };
  friday: { enabled: boolean; timeBlocks: TimeBlock[] };
  saturday: { enabled: boolean; timeBlocks: TimeBlock[] };
  sunday: { enabled: boolean; timeBlocks: TimeBlock[] };
}

interface Room {
  id: string;
  name: string;
  services: string[];
  availability: RoomAvailability;
  active: boolean;
}

interface OpeningHours {
  monday: { enabled: boolean; open: string; close: string };
  tuesday: { enabled: boolean; open: string; close: string };
  wednesday: { enabled: boolean; open: string; close: string };
  thursday: { enabled: boolean; open: string; close: string };
  friday: { enabled: boolean; open: string; close: string };
  saturday: { enabled: boolean; open: string; close: string };
  sunday: { enabled: boolean; open: string; close: string };
}

interface Location {
  id: string;
  name: string;
  address: string;
  phone?: string;
  email?: string;
  openingHours?: OpeningHours;
  rooms: Room[];
  active: boolean;
}

interface Service {
  id: string;
  name: string;
  price: string;
  duration: number;
  color: string;
  active: boolean;
}

interface LocationsSettingsTabProps {
  locations: Location[];
  services: Service[];
  isDarkMode?: boolean;
  onAddLocation: () => void;
  onEditLocation: (location: Location) => void;
  onDeleteLocation: (locationId: string) => void;
  onAddRoom: (location: Location) => void;
  onEditRoom: (location: Location, room: Room) => void;
  onDeleteRoom: (locationId: string, roomId: string) => void;
  onNavigateToBilling?: () => void;
}

interface LocationFormPanelProps {
  location?: Location;
  services: Service[];
  isDarkMode?: boolean;
  onSave: (locationData: Partial<Location>) => void;
  onBack: () => void;
  onAddRoom: (id) => void;
  isUpdateLocationLoading?: boolean;
  confirmDelete?: (action: () => void, message?: string) => void;
}

interface RoomFormInLocationProps {
  location?: Location;
  room?: Room;
  services: Service[];
  isDarkMode?: boolean;
  onSave: (roomData: Partial<Room>) => void;
  onCancel: () => void;
}
export interface LocationPrefill extends TitanPharmacy {
  titanCode: string;
}

// Main LocationsSettingsTab component
export function LocationsSettingsTab({
  locations,
  services,
  isDarkMode,
  onAddLocation,
  onEditLocation,
  onDeleteLocation,
  onAddRoom,
  onEditRoom,
  onDeleteRoom,
  onNavigateToBilling,
}: LocationsSettingsTabProps) {
  const [showTitanPrompt, setShowTitanPrompt] = useState(false);

  const {
    locationState,
    onboardingVerificationState,
    companyServiceBillingState,
    appState,
    generalsState,
  } = useTitanApp();
  const { isPharmacyServiceLoading, companyPharmaciesCount } = locationState;
  const { titanCodeResponse } = onboardingVerificationState || {};
  const { companyLedgerSummary } = companyServiceBillingState || {};
  const { currentCompanyId, companyId, features } = appState || {};
  const { generalData } = generalsState || {};

  const { ifBillingEnabled, ifAIConsultationEnabled } = features || {};

  const canAddSites = ifAIConsultationEnabled;
  const showBillingFeatures =
    ifAIConsultationEnabled &&
    (ifBillingEnabled || generalData?.companySubscriptionStatus === 2);

  const siteLicenseItem = companyLedgerSummary?.items?.find(
    (x) => x.ledgerItemType === "SiteLicense",
  );

  const totalSiteLicences =
    siteLicenseItem?.details?.reduce(
      (sum, d) => sum + (d?.licensesPurchased ?? 0),
      0,
    ) ?? 0;
  const activeSites = Number(companyPharmaciesCount) || 0;
  const remainingSiteLicences = Math.max(0, totalSiteLicences - activeSites);
  const atSiteLimit = activeSites >= totalSiteLicences;
  if (isPharmacyServiceLoading) {
    return <LocationsSettingsSkeleton />;
  }
  const handleStartAddLocation = () => {
    setShowTitanPrompt(true);
  };

  const handleTitanResolved = (prefill: LocationPrefill) => {
    setShowTitanPrompt(false);
    onAddLocation(prefill);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Sites
          </h2>
          {showBillingFeatures && (
            <p
              className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {activeSites} of {totalSiteLicences} site licence
              {totalSiteLicences === 1 ? "" : "s"} in use •{" "}
              {remainingSiteLicences} remaining
            </p>
          )}
        </div>
        <Button
          size="sm"
          className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleStartAddLocation}
          disabled={!canAddSites || (showBillingFeatures && atSiteLimit)}
          title={
            !canAddSites
              ? "Site management is not available in your current plan"
              : showBillingFeatures && atSiteLimit
                ? "You have used all of your site licences. Purchase another site licence in Billing."
                : ""
          }
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Site
        </Button>
      </div>
      {showBillingFeatures && atSiteLimit && (
        <div
          className={`flex items-start gap-3 p-4 rounded-lg border transition-colors duration-300 ${
            isDarkMode
              ? "border-red-600 bg-red-900/20"
              : "border-red-300 bg-red-50"
          }`}
        >
          <AlertTriangle
            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
              isDarkMode ? "text-red-400" : "text-red-500"
            }`}
          />
          <div className="flex-1">
            <p
              className={`text-sm font-medium ${
                isDarkMode ? "text-red-300" : "text-red-800"
              }`}
            >
              Site licence limit reached
            </p>
            <p
              className={`text-sm mt-1 ${
                isDarkMode ? "text-red-400" : "text-red-700"
              }`}
            >
              You have used all {totalSiteLicences} of your site licence
              {totalSiteLicences === 1 ? "" : "s"}. To add another site, please
              purchase another site in Billing.
            </p>
            {onNavigateToBilling && (
              <Button
                onClick={onNavigateToBilling}
                size="sm"
                className="mt-3 bg-red-600 hover:bg-red-700 text-white"
              >
                Go to Billing to Add a Site
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="space-y-6">
        {locations.map((location) => (
          <div
            key={location.id}
            className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-white"
            }`}
          >
            {/* Location Header */}
            <div
              className={`p-4 transition-colors duration-300 ${
                isDarkMode ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3
                      className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {location.name}
                    </h3>
                    <Badge variant={location.active ? "default" : "secondary"}>
                      {location.active ? "ACTIVE" : "INACTIVE"}
                    </Badge>
                  </div>
                  <p
                    className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {location.address}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditLocation(location)}
                    className={
                      isDarkMode
                        ? "text-gray-300 hover:text-white hover:bg-gray-600"
                        : ""
                    }
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  {/* <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteLocation(location.id)}
                    className={
                      isDarkMode
                        ? "text-red-400 hover:text-red-300 hover:bg-gray-600"
                        : "text-red-500 hover:text-red-600"
                    }
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button> */}
                </div>
              </div>
            </div>

            {/* Rooms Section */}
            <div
              className={`p-4 transition-colors duration-300 ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h4
                  className={`font-medium flex items-center gap-2 transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  <DoorOpen className="w-4 h-4" />
                  Rooms ({location.rooms?.length || 0})
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAddRoom(location)}
                  disabled={!location.active}
                  className={
                    isDarkMode
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : ""
                  }
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Room
                </Button>
              </div>

              {location.rooms && location.rooms.length > 0 ? (
                <div className="space-y-3">
                  {location.rooms.map((room) => (
                    <div
                      key={room.id}
                      className={`border rounded-lg p-3 transition-colors duration-300 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-700"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5
                              className={`font-medium transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {room.name}
                            </h5>
                            <Badge
                              variant={room.active ? "default" : "secondary"}
                              className="text-xs"
                            >
                              {room.active ? "ACTIVE" : "INACTIVE"}
                            </Badge>
                          </div>
                          <div
                            className={`text-sm mb-2 transition-colors duration-300 ${
                              isDarkMode ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            <span className="font-medium">Services:</span>{" "}
                            {room.services.length > 0
                              ? room.services.join(", ")
                              : "No services assigned"}
                          </div>
                          <div
                            className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Available:{" "}
                            {Object.entries(room.availability)
                              .filter(([day, schedule]) => schedule.enabled)
                              .map(
                                ([day]) =>
                                  day.charAt(0).toUpperCase() + day.slice(1),
                              )
                              .join(", ") || "No availability set"}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditRoom(location, room)}
                            className={
                              isDarkMode
                                ? "text-gray-300 hover:text-white hover:bg-gray-600"
                                : ""
                            }
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteRoom(location.id, room.id)}
                            className={
                              isDarkMode
                                ? "text-red-400 hover:text-red-300 hover:bg-gray-600"
                                : "text-red-500 hover:text-red-600"
                            }
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className={`text-center py-6 transition-colors duration-300 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  <DoorOpen
                    className={`w-8 h-8 mx-auto mb-2 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  />
                  <p className="text-sm">No rooms added yet</p>
                  <p className="text-xs">
                    Add rooms to organize services by space
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <TitanCodePromptDialog
        open={showTitanPrompt}
        titanCodeResponse={titanCodeResponse}
        existingCodes={
          locations
            .map((l) => l.titanCode?.toUpperCase())
            .filter(Boolean) as string[]
        }
        isDarkMode={isDarkMode}
        onCancel={() => setShowTitanPrompt(false)}
        onResolved={handleTitanResolved}
        currentCompanyId={currentCompanyId}
        companyId={companyId}
      />
    </div>
  );
}

function TitanCodePromptDialog({
  open,
  titanCodeResponse,
  existingCodes,
  isDarkMode = false,
  onCancel,
  onResolved,
  currentCompanyId,
  companyId,
}: {
  open: boolean;
  titanCodeResponse: any;
  existingCodes: string[];
  isDarkMode?: boolean;
  onCancel: () => void;
  onResolved: (prefill: LocationPrefill) => void;
  currentCompanyId?: string;
  companyId?: string;
}) {
  const [code, setCode] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setCode("");
      setError(null);
      setIsValidating(false);
      setPendingCode(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (isValidating && pendingCode && titanCodeResponse) {
      let isSuccess = false;
      let message = "";

      if (typeof titanCodeResponse === "string") {
        isSuccess = titanCodeResponse === "Added Successfully";
        message = titanCodeResponse;
      } else if (typeof titanCodeResponse === "object") {
        isSuccess = titanCodeResponse?.success === true;
        message =
          titanCodeResponse?.message || titanCodeResponse?.response || "";
      }

      if (isSuccess) {
        const handleSuccess = async () => {
          try {
            await Promise.all([
              getPharmaciesByCompanyId(
                currentCompanyId !== "" ? currentCompanyId : companyId,
              ),
              getCompanyPharmaciesCount(
                currentCompanyId !== "" ? currentCompanyId : companyId,
              ),
            ]);
          } catch (error) {
            console.error("Error refreshing pharmacies:", error);
          }
          setIsValidating(false);
          setPendingCode(null);
          onCancel();
        };
        handleSuccess();
      } else {
        const errorMessage =
          message ||
          "Unable to add site with this TITAN code. Please check and try again.";
        setError(errorMessage);
        setIsValidating(false);
        setPendingCode(null);
      }
    }
  }, [
    titanCodeResponse,
    isValidating,
    pendingCode,
    onResolved,
    currentCompanyId,
    companyId,
  ]);

  const handleAddFromInvite = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setError("Please enter a TITAN code.");
      return;
    }
    if (existingCodes.includes(trimmed)) {
      setError("A location with this TITAN code has already been added.");
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const response = await updateIsActiveByUserEmailCode(trimmed);

      let isSuccess = false;
      if (typeof response === "string") {
        isSuccess = response === "Added Successfully";
      } else if (typeof response === "object") {
        isSuccess = response?.success === true;
      }

      if (isSuccess) {
        try {
          await Promise.all([
            getPharmaciesByCompanyId(
              currentCompanyId !== "" ? currentCompanyId : companyId,
            ),
            getCompanyPharmaciesCount(
              currentCompanyId !== "" ? currentCompanyId : companyId,
            ),
          ]);
        } catch (error) {
          console.error("Error refreshing pharmacies:", error);
        }
        setIsValidating(false);
        onCancel();
      } else {
        const message =
          typeof response === "string"
            ? response
            : response?.message ||
              "Unable to add site with this TITAN code. Please check and try again.";
        setError(message);
        setIsValidating(false);
      }
    } catch (e) {
      setError(
        "Something went wrong while adding this TITAN code. Please try again.",
      );
      setIsValidating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isValidating) onCancel();
      }}
    >
      <DialogContent
        className={
          isDarkMode
            ? "sm:max-w-md bg-gray-900 border-gray-700 text-white"
            : "sm:max-w-md"
        }
      >
        <DialogHeader>
          <DialogTitle>Enter TITAN code</DialogTitle>
          <DialogDescription
            className={isDarkMode ? "text-gray-400" : undefined}
          >
            Enter your TITAN activation code to add this site to your account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label htmlFor="titan-code-input">TITAN code</Label>
            <Input
              id="titan-code-input"
              autoFocus
              maxLength={LOCATION_LIMITS.titanCode}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isValidating) {
                  e.preventDefault();
                  handleAddFromInvite();
                }
              }}
              placeholder="e.g. TITAN001"
              disabled={isValidating}
              className={
                isDarkMode
                  ? "bg-gray-800 border-gray-600 text-white uppercase"
                  : "uppercase"
              }
            />
            <CharLimitMessage value={code} max={LOCATION_LIMITS.titanCode} />
          </div>

          {error && (
            <div
              className={`flex items-start gap-2 p-3 rounded-md border text-sm ${
                isDarkMode
                  ? "border-red-700 bg-red-900/30 text-red-300"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium">TITAN code not recognised</p>
                <p className="mt-0.5">{error}</p>
                <p className="mt-1 text-xs opacity-90">
                  Double-check the code with your Titan administrator, or skip
                  and enter the location details manually.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isValidating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAddFromInvite}
            disabled={isValidating || !code.trim()}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            {isValidating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Adding…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Add
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// LocationFormPanel component for adding/editing locations
export function LocationFormPanel({
  location,
  services,
  isDarkMode,
  onSave,
  onBack,
  onAddRoom,
  isUpdateLocationLoading,
  confirmDelete,
}: LocationFormPanelProps) {
  const { appState } = useTitanApp();
  const { companyId } = appState || {};
  const defaultLocation = {
    name: "",
    address: "",
    phone: "",
    email: "",
    openingHours: {
      monday: { enabled: true, open: "09:00", close: "17:00" },
      tuesday: { enabled: true, open: "09:00", close: "17:00" },
      wednesday: { enabled: true, open: "09:00", close: "17:00" },
      thursday: { enabled: true, open: "09:00", close: "17:00" },
      friday: { enabled: true, open: "09:00", close: "17:00" },
      saturday: { enabled: true, open: "09:00", close: "13:00" },
      sunday: { enabled: true, open: "10:00", close: "16:00" },
    },
    rooms: [],
    active: true,
  };

  const [formData, setFormData] = useState(() => {
    const data = location || defaultLocation;
    return {
      ...data,
      rooms: data.rooms || [],
      openingHours: data.openingHours || defaultLocation.openingHours,
    };
  });
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const dayNames = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ] as const;
  const dayLabels = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateOpeningHours = (
    day: keyof OpeningHours,
    field: string,
    value: any,
  ) => {
    setFormData((prev) => {
      if (field === "enabled") {
        const newDayData = { ...prev.openingHours[day], enabled: value };

        if (
          value === true &&
          (!prev.openingHours[day].open || !prev.openingHours[day].close)
        ) {
          newDayData.open = "09:00";
          newDayData.close = "17:00";
        }

        if (value === false) {
          newDayData.open = null;
          newDayData.close = null;
        }

        return {
          ...prev,
          openingHours: {
            ...prev.openingHours,
            [day]: newDayData,
          },
        };
      }

      return {
        ...prev,
        openingHours: {
          ...prev.openingHours,
          [day]: { ...prev.openingHours[day], [field]: value },
        },
      };
    });
  };

  // Room management functions
  const addRoom = () => {
    setEditingRoom(null);
    setShowRoomForm(true);
  };

  const editRoom = (room: Room) => {
    setEditingRoom(room);
    setShowRoomForm(true);
  };

  const saveRoom = async (roomData: Partial<Room>) => {
    if (editingRoom) {
      // Edit existing room
      setFormData((prev) => ({
        ...prev,
        rooms: prev.rooms.map((room) =>
          room.id === editingRoom.id
            ? ({ ...roomData, id: editingRoom.id } as Room)
            : room,
        ),
      }));
    } else {
      // Add new room
      const newRoom: Room = {
        ...roomData,
        id: "",
      } as Room;
      setFormData((prev) => ({
        ...prev,
        rooms: [...prev.rooms, newRoom],
      }));
      const dayMap: Record<string, number> = {
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
        sunday: 7,
      };

      const mapAvailabilityToSlots = (availability: RoomAvailability) => {
        if (!availability) return [];

        const result: {
          dayOfWeek: number;
          slots: { startTime: string; endTime: string }[];
        }[] = [];

        for (const [day, value] of Object.entries(availability)) {
          const dayOfWeek = dayMap[day.toLowerCase()];
          if (dayOfWeek === undefined) continue;

          if (!value?.enabled || !Array.isArray(value.timeBlocks)) continue;

          const slots = value.timeBlocks
            .filter((tb) => tb.from && tb.to)
            .map((tb) => ({
              startTime: tb.from.length > 5 ? tb.from.slice(0, 5) : tb.from,
              endTime: tb.to.length > 5 ? tb.to.slice(0, 5) : tb.to,
            }));

          if (slots.length > 0) {
            result.push({ dayOfWeek, slots });
          }
        }

        return result;
      };

      const selectedServices = services.filter((service) =>
        newRoom.services?.includes(service.name),
      );

      const serviceModels = selectedServices?.map((service) => ({
        _id: service?.id,
        ServiceName: service?.name,
        ServiceDescription: service?.description || "",
        ServiceCharges: service?.price || "0",
        ServiceDuration: service?.duration || 0,
        ServiceColor: service?.color || "#000000",
        ServiceType: service?.serviceType || 2,
        ServiceCode: 0,
        IsActive: service?.active || true,
        ServiceChargesType: 1,
        IsDefault: false,
        CompanyID: "",
        CreatedOn: new Date().toISOString(),
        UpdatedOn: new Date().toISOString(),
        IsOnlineBooking: false,
        allowGroupBookings: false,
        maxGroupSize: 0,
      }));

      const payload = {
        PharmacyID: formData?.id,
        PharmacyRoom: {
          roomName: newRoom?.name,
          services: serviceModels,
          availability: mapAvailabilityToSlots(newRoom?.availability),
          active: newRoom?.active,
        },
      };
      const savedRoomResponse = await addPharmacyRoom(payload);
      const savedRoomId =
        savedRoomResponse?._id ||
        savedRoomResponse?.id ||
        savedRoomResponse?.data?._id ||
        savedRoomResponse?.data?.id ||
        savedRoomResponse?.room?._id ||
        savedRoomResponse?.room?.id ||
        "";

      const latestPharmacies = await getPharmaciesByCompanyId(companyId);
      const targetLocationId = formData?.id || location?.id;
      const latestLocation = latestPharmacies?.find(
        (pharmacy) => pharmacy?.pharmacyID === targetLocationId,
      );

      const buildRoomAvailabilityFromApi = (
        availability: any[] = [],
      ): RoomAvailability => {
        const defaultAvailability: RoomAvailability = {
          monday: { enabled: false, timeBlocks: [] },
          tuesday: { enabled: false, timeBlocks: [] },
          wednesday: { enabled: false, timeBlocks: [] },
          thursday: { enabled: false, timeBlocks: [] },
          friday: { enabled: false, timeBlocks: [] },
          saturday: { enabled: false, timeBlocks: [] },
          sunday: { enabled: false, timeBlocks: [] },
        };

        const dayMap: Record<number, keyof RoomAvailability> = {
          1: "monday",
          2: "tuesday",
          3: "wednesday",
          4: "thursday",
          5: "friday",
          6: "saturday",
          7: "sunday",
        };

        availability.forEach((day) => {
          const dayKey = dayMap[day?.dayOfWeek];
          if (!dayKey) return;

          const timeBlocks = (day?.slots || []).map((slot: any) => ({
            from: slot?.startTime || "",
            to: slot?.endTime || "",
          }));

          defaultAvailability[dayKey] = {
            enabled: timeBlocks.length > 0,
            timeBlocks,
          };
        });

        return defaultAvailability;
      };

      const refreshedRooms = (latestLocation?.pharmacyRooms || []).map(
        (room: any) => ({
          id: room?._id || room?.id || "",
          name: room?.roomName || "",
          services: Array.isArray(room?.services)
            ? room.services
                .map(
                  (service: any) =>
                    service?.serviceName || service?.ServiceName || "",
                )
                .filter(Boolean)
            : [],
          availability: buildRoomAvailabilityFromApi(room?.availability),
          active: room?.active ?? true,
        }),
      );

      setFormData((prev) => ({
        ...prev,
        rooms:
          refreshedRooms.length > 0
            ? refreshedRooms
            : prev.rooms.map((room) =>
                room.id === "" && room.name === newRoom.name
                  ? ({ ...room, id: savedRoomId } as Room)
                  : room,
              ),
      }));
    }
    setShowRoomForm(false);
    setEditingRoom(null);
  };

  const deleteRoom = (roomId: string) => {
    if (roomId) {
      const room = formData.rooms.find((r) => r.id === roomId);
      if (room?.services?.length > 0) {
        showError(
          "This room cannot be deleted because it has services linked to it. Please remove the services from the room first.",
        );
        return;
      }
      setFormData((prev) => ({
        ...prev,
        rooms: prev.rooms.filter((room) => room.id !== roomId),
      }));
    }
  };

  const handleSubmit = () => {
    if (formData.name.trim() && formData.address.trim()) {
      onSave(formData);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div
        className={`sticky top-0 z-10 flex items-center gap-4 pb-4 border-b transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          ← Back
        </Button>
        <h2
          className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          {location ? "Edit Location" : "Add New Location"}
        </h2>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Basic Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="location-name">Location Name</Label>
              <Input
                id="location-name"
                value={formData.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="Enter location name"
                maxLength={LOCATION_LIMITS.name}
              />
              <CharLimitMessage value={formData.name} max={LOCATION_LIMITS.name} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location-phone">Phone Number</Label>
              <Input
                id="location-phone"
                value={formData.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                placeholder="Enter phone number"
                maxLength={LOCATION_LIMITS.phone}
              />
              <CharLimitMessage value={formData.phone} max={LOCATION_LIMITS.phone} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="location-address">Address</Label>
              <Textarea
                id="location-address"
                value={formData.address}
                onChange={(e) => updateField("address", e.target.value)}
                placeholder="Enter full address"
                rows={3}
                maxLength={LOCATION_LIMITS.address}
              />
              <CharLimitMessage value={formData.address} max={LOCATION_LIMITS.address} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location-email">Email Address</Label>
              <Input
                id="location-email"
                type="email"
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="Enter email address"
                maxLength={LOCATION_LIMITS.email}
              />
              <CharLimitMessage value={formData.email} max={LOCATION_LIMITS.email} />
            </div>
          </div>

          <div className="flex items-center mt-4 space-x-2">
            <Switch
              id="location-active"
              checked={formData.active}
              onCheckedChange={(checked) => updateField("active", checked)}
            />
            <Label htmlFor="location-active">Active Location</Label>
          </div>
        </div>

        {/* Opening Hours */}
        <div>
          <h3
            className={`font-medium mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Opening Hours
          </h3>
          <div className="space-y-3">
            {dayNames.map((day, index) => (
              <div
                key={day}
                className={`flex items-center gap-4 p-3 border rounded-lg transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-center gap-2 min-w-[100px]">
                  <Switch
                    checked={formData.openingHours[day].enabled}
                    onCheckedChange={(checked) =>
                      updateOpeningHours(day, "enabled", checked)
                    }
                  />
                  <Label className="text-sm font-medium">
                    {dayLabels[index]}
                  </Label>
                </div>

                {formData.openingHours[day].enabled && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={formData.openingHours[day].open}
                      onChange={(e) =>
                        updateOpeningHours(day, "open", e.target.value)
                      }
                      className={`w-24 ${
                        isDarkMode
                          ? "bg-gray-600 border-gray-500 text-white"
                          : ""
                      }`}
                    />
                    <span
                      className={`transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-500"
                      }`}
                    >
                      to
                    </span>
                    <Input
                      type="time"
                      value={formData.openingHours[day].close}
                      onChange={(e) =>
                        updateOpeningHours(day, "close", e.target.value)
                      }
                      className={`w-24 ${
                        isDarkMode
                          ? "bg-gray-600 border-gray-500 text-white"
                          : ""
                      }`}
                    />
                  </div>
                )}

                {!formData.openingHours[day].enabled && (
                  <span
                    className={`text-sm italic transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Closed
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Room Management */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3
              className={`font-medium flex items-center gap-2 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              <DoorOpen className="w-4 h-4" />
              Rooms ({formData.rooms.length})
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                await onAddRoom(formData);
                addRoom();
              }}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Room
            </Button>
          </div>

          {formData.rooms.length > 0 ? (
            <div className="space-y-3">
              {formData.rooms.map((room) => (
                <div
                  key={room.id}
                  className={`border rounded-lg p-3 transition-colors duration-300 ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-700"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h5
                          className={`font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {room.name}
                        </h5>
                        <Badge
                          variant={room.active ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {room.active ? "ACTIVE" : "INACTIVE"}
                        </Badge>
                      </div>
                      <div
                        className={`text-sm mb-2 transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        <span className="font-medium">Services:</span>{" "}
                        {room.services.length > 0
                          ? room.services.join(", ")
                          : "No services assigned"}
                      </div>
                      <div
                        className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Available:{" "}
                        {(
                          Object.entries(room.availability) as [
                            string,
                            { enabled: boolean },
                          ][]
                        )
                          .filter(([day, schedule]) => schedule.enabled)
                          .map(
                            ([day]) =>
                              day.charAt(0).toUpperCase() + day.slice(1),
                          )
                          .join(", ") || "No availability set"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          await onAddRoom(formData);
                          editRoom(room);
                        }}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirmDelete) {
                            confirmDelete(
                              () => deleteRoom(room.id),
                              `Are you sure you want to delete the room "${room.name}"?`,
                            );
                          } else {
                            // Fallback if confirmDelete is not provided
                            deleteRoom(room.id);
                          }
                        }}
                        className={
                          isDarkMode
                            ? "text-red-400 hover:text-red-300 hover:bg-gray-600"
                            : "text-red-500 hover:text-red-600"
                        }
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className={`text-center py-6 border rounded-lg transition-colors duration-300 ${
                isDarkMode
                  ? "text-gray-400 border-gray-600 bg-gray-700"
                  : "text-gray-500 border-gray-200 bg-gray-50"
              }`}
            >
              <DoorOpen
                className={`w-8 h-8 mx-auto mb-2 transition-colors duration-300 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              />
              <p className="text-sm">No rooms added yet</p>
              <p className="text-xs">Add rooms to organize services by space</p>
            </div>
          )}
        </div>
      </div>

      {/* Room Form Modal */}
      {showRoomForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4 transition-colors duration-300 ${
              isDarkMode ? "bg-gray-900" : "bg-white"
            }`}
          >
            <RoomFormInLocation
              location={location}
              room={editingRoom || undefined}
              services={services}
              onSave={saveRoom}
              onCancel={() => {
                setShowRoomForm(false);
                setEditingRoom(null);
              }}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div
        className={`flex gap-2 justify-end pt-4 border-t transition-colors duration-300 ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <Button variant="outline" onClick={onBack}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!formData.name.trim() || !formData.address.trim()}
          className="bg-slate-900 hover:bg-slate-800"
        >
          <Save className="w-4 h-4 mr-2" />
          {isUpdateLocationLoading ? "Please wait..." : "Update Location"}
        </Button>
      </div>
    </div>
  );
}

// RoomFormInLocation component for adding/editing rooms
export function RoomFormInLocation({
  location,
  room,
  services,
  isDarkMode,
  onSave,
  onCancel,
}: RoomFormInLocationProps) {
  const { appState } = useTitanApp();

  const { currentPharmacyId } = appState || {};
  const buildDefaultAvailabilityFromOpeningHours = (openingHours) => {
    const availability = {};

    Object.keys(openingHours).forEach((day) => {
      if (openingHours[day]?.enabled) {
        availability[day] = {
          enabled: true,
          timeBlocks: [
            {
              from: openingHours[day].open,
              to: openingHours[day].close,
            },
          ],
        };
      } else {
        availability[day] = {
          enabled: false,
          timeBlocks: [],
        };
      }
    });

    return availability;
  };
  const defaultRoom = {
    name: "",
    services: [],
    availability: buildDefaultAvailabilityFromOpeningHours(
      location.openingHours,
    ),
    active: true,
  };

  const [formData, setFormData] = useState(room || defaultRoom);

  const dayNames = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ] as const;
  const dayLabels = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateAvailability = (
    day: keyof RoomAvailability,
    field: string,
    value: any,
  ) => {
    if (field === "enabled" && !location.openingHours?.[day]?.enabled) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value,
        },
      },
    }));
  };

  const addTimeBlock = (day: keyof RoomAvailability) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          timeBlocks: [
            ...prev.availability[day].timeBlocks,
            { from: "09:00", to: "17:00" },
          ],
        },
      },
    }));
  };

  const removeTimeBlock = (day: keyof RoomAvailability, blockIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          timeBlocks: prev.availability[day].timeBlocks.filter(
            (_, index) => index !== blockIndex,
          ),
        },
      },
    }));
  };

  const updateTimeBlock = (
    day: keyof RoomAvailability,
    blockIndex: number,
    field: string,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          timeBlocks: prev.availability[day].timeBlocks.map((block, index) =>
            index === blockIndex ? { ...block, [field]: value } : block,
          ),
        },
      },
    }));
  };

  const toggleService = (serviceName: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(serviceName)
        ? prev.services.filter((s) => s !== serviceName)
        : [...prev.services, serviceName],
    }));
  };

  const handleSubmit = (loc) => {
    if (formData.name.trim()) {
      onSave(formData);
    }
  };
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      name: room?.name || "",
    },
  });

  const onSubmit = async (data: any) => {
    const combinedData = {
      ...formData,
      ...data,
    };

    onSave(combinedData);
  };
  return (
    <form onSubmit={handleFormSubmit(onSubmit)}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div
          className={`flex items-center justify-between pb-4 border-b transition-colors duration-300 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h3
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {room ? "Edit Room" : "Add New Room"}
          </h3>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            ✕
          </Button>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Room Information */}
          <div>
            <h4
              className={`font-medium mb-3 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Room Information
            </h4>
            <div className="grid grid-cols-2 gap-4 pl-1">
              <div className="space-y-2">
                <Label htmlFor="room-name">Room Name</Label>
                <Input
                  id="room-name"
                  {...register("name", {
                    required: "Room name is required",
                    minLength: { value: 1, message: "Too short" },
                  })}
                  placeholder="Enter room name"
                  maxLength={LOCATION_LIMITS.roomName}
                />
                <CharLimitMessage value={watch("name")} max={LOCATION_LIMITS.roomName} />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>
              <div className="flex items-center mt-8 space-x-2">
                <Switch
                  id="room-active"
                  checked={formData.active}
                  onCheckedChange={(checked) => updateField("active", checked)}
                />
                <Label htmlFor="room-active">Active Room</Label>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4
              className={`font-medium mb-3 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Available Services
            </h4>
            <div className="grid grid-cols-1 gap-2 pl-1 overflow-y-auto max-h-32 custom-scrollbar">
              {services
                .filter(
                  (service) =>
                    service.serviceType !== 1 && service.serviceType !== 3,
                )
                .map((service) => (
                  <div
                    key={service.id}
                    className={`flex items-center space-x-3 p-2 border rounded transition-colors duration-300 ${
                      isDarkMode
                        ? "border-gray-600 hover:bg-gray-700"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`service-${service.id}`}
                      checked={formData.services.includes(service.name)}
                      onChange={() => toggleService(service.name)}
                      className="rounded"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor={`service-${service.id}`}
                        className={`text-sm font-medium cursor-pointer flex items-center gap-2 transition-colors duration-300 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: service.color }}
                        />
                        {service.name}
                      </label>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Availability - Compact */}
          <div>
            <h4
              className={`font-medium mb-3 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Availability
            </h4>
            <div className="grid grid-cols-1 gap-2 pl-1">
              {dayNames.map((day, index) => (
                <div
                  key={day}
                  className={`flex items-center gap-3 p-2 border rounded transition-colors duration-300 ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-700"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <Switch
                    checked={formData?.availability[day].enabled}
                    onCheckedChange={(checked) =>
                      updateAvailability(day, "enabled", checked)
                    }
                  />
                  <Label className="font-medium text-sm min-w-[70px]">
                    {dayLabels[index]}
                  </Label>

                  {formData?.availability[day].enabled && (
                    <div className="flex items-center flex-1 gap-1">
                      {formData.availability[day].timeBlocks.map(
                        (block, blockIndex) => (
                          <div
                            key={blockIndex}
                            className="flex items-center gap-1"
                          >
                            <Input
                              type="time"
                              value={block.from}
                              onChange={(e) =>
                                updateTimeBlock(
                                  day,
                                  blockIndex,
                                  "from",
                                  e.target.value,
                                )
                              }
                              className={`w-21 h-7 text-xs ${
                                isDarkMode
                                  ? "bg-gray-600 border-gray-500 text-white"
                                  : ""
                              }`}
                            />
                            <span className="text-xs">-</span>
                            <Input
                              type="time"
                              value={block.to}
                              onChange={(e) =>
                                updateTimeBlock(
                                  day,
                                  blockIndex,
                                  "to",
                                  e.target.value,
                                )
                              }
                              className={`w-21 h-7 text-xs ${
                                isDarkMode
                                  ? "bg-gray-600 border-gray-500 text-white"
                                  : ""
                              }`}
                            />
                            {formData.availability[day].timeBlocks.length >
                              1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTimeBlock(day, blockIndex)}
                                className="w-5 h-5 p-0"
                              >
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            )}
                          </div>
                        ),
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addTimeBlock(day)}
                        className="h-5 px-1 text-xs"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          className={`flex gap-2 justify-end pt-4 border-t transition-colors duration-300 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
            <Save className="w-4 h-4 mr-2" />
            {room ? "Update Room" : "Save Room"}
          </Button>
        </div>
      </div>
    </form>
  );
}
